import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import {
  faDiscord,
  faInstagram,
  faTwitter,
} from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import { MobileContentVisibility } from '@components/MobileContentVisibility';
import { SoundLogo } from '@components/icons/SoundLogo';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { Mirror } from '@components/main-components/media/icons/Mirror';
import { ApplicationRejected } from '@components/modals/ArtistApplication/ApplicationRejected';
import { ApplicationSubmittedModal } from '@components/modals/ArtistApplication/ApplicationSubmitted';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { ArtistApplicationStatus } from '@graphql/reactQuery';
import { useAuthArtistApplicationStatus } from '@hooks/useAuthArtistApplicationStatus';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { EXTERNAL_PATHS, HELP_PATHS, NAV_LINKS } from '@utils/constants';
import type { SharedProps } from '../index';
import { LearnMore } from './LearnMore';

export const Footer = (props: SharedProps) => {
  const isHydrated = useIsHydrated();
  const { activeTrackId } = useAudioController();
  const { pathname } = useRouter();

  const isLandingPage = pathname === NAV_LINKS.Home;

  return (
    <>
      {isLandingPage && (
        <MobileContentVisibility visibility="auto" sizeY={244}>
          <div className="mx-auto  flex w-full px-0 lg:mb-5 lg:px-6 2xl:max-w-content">
            <LearnMore {...props} />
          </div>
        </MobileContentVisibility>
      )}

      <MobileContentVisibility visibility="auto" sizeY={244}>
        <div className="flex flex-col items-center justify-center bg-base100">
          <div
            className={clsx(
              'flex w-full  flex-col items-start gap-y-8 px-4 pt-8 lg:max-w-content lg:flex-row lg:justify-between lg:px-6 lg:pb-16 lg:pt-12 ',
              isHydrated && activeTrackId
                ? 'mb-32 pb-8 lg:mb-[94px] lg:pb-16'
                : 'pb-24 lg:mb-0 lg:pb-16',
            )}
          >
            <SoundSection />
            <div className="flex flex-col gap-y-8 lg:flex-row">
              <div>
                <IosApp />
              </div>
              <div className="flex justify-between lg:flex-row lg:gap-2">
                <CompanySection />
                <ResourcesSection />
              </div>
              <FollowUsSection />
            </div>
          </div>
        </div>
      </MobileContentVisibility>
    </>
  );
};

const SoundSection = () => {
  return (
    <div className="flex h-fit flex-col gap-y-6 lg:h-40 lg:justify-between">
      <AccessibleLink className="w-fit" pathnameUri="/">
        <SoundLogo color="black" />
      </AccessibleLink>
      <div className="flex flex-col whitespace-nowrap font-base text-base-s text-base800 opacity-80">
        <p>© Copyright {new Date().getFullYear()} Sound.xyz. All rights reserved.</p>
        <div className="flex gap-2">
          <AccessibleLink absoluteUri={EXTERNAL_PATHS.TOS} type="external">
            Terms of Service
          </AccessibleLink>
          <span>|</span>
          <AccessibleLink absoluteUri={EXTERNAL_PATHS.Privacy} type="external">
            Privacy Policy
          </AccessibleLink>
        </div>
      </div>
    </div>
  );
};
const IosApp = () => {
  return (
    <div className="flex min-w-[174px] flex-col gap-y-4 md:min-w-[142px] lg:min-w-[168px]">
      <h3 className="font-title text-title-xs font-medium text-base800">iOS App</h3>
      <AccessibleLink
        absoluteUri="https://apps.apple.com/us/app/sound-xyz/id6446258640"
        type="external"
      >
        <NextFutureImage
          alt="Download on the App Store"
          src="/icons/apple-ios-download.svg"
          width={118}
          height={38}
        />
      </AccessibleLink>
    </div>
  );
};

const CompanySection = () => {
  const { authUser } = useAuth();
  const { hasApplied, status } = useAuthArtistApplicationStatus();

  const hasPendingApplication = status === ArtistApplicationStatus.Pending;
  const rejectedApplication = status === ArtistApplicationStatus.Rejected;
  const isArtist = !!authUser?.artist;

  const { openModal } = ModalContext.useContainer();
  const onForArtistsClick = () => {
    if (!hasApplied && !hasPendingApplication) return;
    trackClickEvent({
      event: 'Join as an Artist Clicked',
      properties: {
        sourceComponent: 'Footer',
      },
    });

    if (hasPendingApplication) {
      trackClickEvent({
        event: 'Artist Application Submitted Modal Opened',
        properties: {
          sourceComponent: 'Footer',
        },
      });
      return openModal(ModalType.ARTIST_APPLICATION_SUBMITTED, {
        body: <ApplicationSubmittedModal nav />,
      });
    }

    if (rejectedApplication) {
      trackClickEvent({
        event: 'Artist Application Rejected Modal Opened',
        properties: {
          sourceComponent: 'Footer',
        },
      });
      return openModal(ModalType.ARTIST_APPLICATION_REJECTED, {
        body: <ApplicationRejected />,
      });
    }
  };
  const forArtists = hasApplied ? (
    <button className="w-fit cursor-pointer outline-none" onClick={onForArtistsClick}>
      For Artists
    </button>
  ) : (
    <AccessibleLink absoluteUri={NAV_LINKS.ArtistApplication} type="external">
      For Artists
    </AccessibleLink>
  );
  return (
    <div className="flex min-w-[174px] flex-col gap-y-4 lg:min-w-[142px] lt:min-w-[168px]">
      <h3 className="font-title text-title-xs font-medium capitalize text-base800">Company</h3>
      <Links>
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Careers} type="external">
          Careers
        </AccessibleLink>
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Market} type="external">
          Market
        </AccessibleLink>
        {!!authUser && !isArtist && forArtists}
      </Links>
    </div>
  );
};
const ResourcesSection = () => {
  return (
    <div className="flex min-w-[174px] flex-col gap-y-4 lg:min-w-[134px] lt:min-w-[168px]">
      <h3 className="font-title text-title-xs font-medium capitalize text-base800">Resources</h3>
      <Links>
        <AccessibleLink pathnameUri={NAV_LINKS.About}>About</AccessibleLink>
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Blog} type="external">
          Blog
        </AccessibleLink>
        <AccessibleLink absoluteUri={HELP_PATHS.Home} type="external">
          FAQ
        </AccessibleLink>
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Docs} type="external">
          Docs
        </AccessibleLink>
      </Links>
    </div>
  );
};
const FollowUsSection = () => {
  return (
    <div className="flex flex-col gap-y-4">
      <h3 className="font-title text-title-xs font-medium capitalize text-base800">Follow Us</h3>
      <div className="flex gap-4 ">
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Twitter} type="external">
          <IconWrapper>
            <FontAwesomeIcon icon={faTwitter} size="xl" />
          </IconWrapper>
        </AccessibleLink>
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Instagram} type="external">
          <IconWrapper>
            <FontAwesomeIcon icon={faInstagram} size="xl" />
          </IconWrapper>
        </AccessibleLink>
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Discord} type="external">
          <IconWrapper>
            <FontAwesomeIcon icon={faDiscord} size="xl" />
          </IconWrapper>
        </AccessibleLink>
        <AccessibleLink absoluteUri={EXTERNAL_PATHS.Blog} type="external">
          <IconWrapper>
            <Mirror fill="#1F1F1F" />
          </IconWrapper>
        </AccessibleLink>
      </div>
    </div>
  );
};

const Links = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col gap-y-3 [&>*]:font-base [&>*]:text-base-m [&>*]:text-base800 [&>*]:opacity-80 hover:[&>*]:opacity-100">
    {children}
  </div>
);

const IconWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center text-base800 opacity-80 hover:opacity-100 [&>svg]:fill-base800 [&>svg]:text-base800">
    {children}
  </div>
);
